import Api from "@/services/Api";
import * as Firebase from "firebase/app";
import "firebase/auth";
import Store from "@/store/store";
import Router from "@/router";
import { ROUTES } from "@/router/routes";

const googleProvider = new Firebase.auth.GoogleAuthProvider();
const fbProvider = new Firebase.auth.FacebookAuthProvider();
const appleProvider = new Firebase.auth.OAuthProvider("apple.com");

appleProvider.addScope("email");
appleProvider.addScope("name");

const errorHandler = error => {
  Store.dispatch("user/setAuthErrors", {
    errors: [error.message],
    clear: true
  });
  throw error;
};

const redirectAfterSignin = () => {
  Store.dispatch("user/setAuthErrors", { errors: [], clear: true });
  const path = Router.currentRoute.query.redirectFrom || ROUTES.style.path;
  Router.push({ path });
};

const redirectAfterSignUp = () => {
  Store.dispatch("user/setAuthErrors", { errors: [], clear: true });
  const path = Router.currentRoute.query.redirectFrom || ROUTES.verify.path;
  Router.push({ path });
};

const redirectAfterLogOut = () => {
  Store.dispatch("user/setAuthErrors", { errors: [], clear: true });
  const path = ROUTES.home.path;
  Router.push({ path });
};

const sendVerificationEmail = () => {
  const user = Firebase.auth().currentUser;

  if (!user) {
    throw "no user";
  }

  if (!user.emailVerified) {
    return user
      .sendEmailVerification({
        url: `${location.protocol}//${location.host}/style`
      })
      .catch(errorHandler);
  }

  return;
};

const clearAuthErrors = () => {
  Store.commit("user/CLEAR_AUTH_ERRORS");
};

const logOut = () => {
  Firebase.auth().signOut();
  redirectAfterLogOut();
  return;
};

const reAuthenticate = () => {
  Firebase.auth().signOut();
  Router.push({ path: ROUTES.signIn.path });
  return;
};

export default {
  clearAuthErrors,
  sendVerificationEmail,
  redirectAfterSignUp,
  redirectAfterSignin,
  redirectAfterLogOut,
  reAuthenticate,
  logOut,
  getFirebaseUser() {
    return Firebase.auth().currentUser;
  },

  getFirebaseUserAsync() {
    return new Promise((resolve, reject) => {
      const unsubscribe = Firebase.auth().onAuthStateChanged(user => {
        unsubscribe();
        resolve(user);
      }, reject);
    });
  },

  createUserWithEmailAndPassword(email, password) {
    return Firebase.auth()
      .createUserWithEmailAndPassword(email, password)
      .then(() => {
        sendVerificationEmail();
        redirectAfterSignUp();
      })
      .catch(errorHandler);
  },

  emailSignIn(email, password) {
    return Firebase.auth()
      .signInWithEmailAndPassword(email, password)
      .then(redirectAfterSignin)
      .catch(errorHandler);
  },

  appleSignIn() {
    return Firebase.auth()
      .signInWithPopup(appleProvider)
      .then(redirectAfterSignin)
      .catch(errorHandler);
  },

  googleSignIn() {
    return Firebase.auth()
      .signInWithPopup(googleProvider)
      .then(redirectAfterSignin)
      .catch(errorHandler);
  },

  facebookSignIn() {
    return Firebase.auth()
      .signInWithPopup(fbProvider)
      .then(redirectAfterSignin)
      .catch(errorHandler);
  },

  updateToken(idToken) {
    Api.defaults.headers.common["Authorization"] = idToken;
  },

  async refreshCurrentUser() {
    const user = this.getFirebaseUser();
    if (user) {
      await user.reload().catch(errorHandler);
      return Firebase.auth().currentUser;
    } else {
      return null;
    }
  },

  async updateCurrentUserEmail(email) {
    let result = { ok: false };

    try {
      const user = this.getFirebaseUser();

      if (user) {
        await user.updateEmail(email).catch(errorHandler);
        if (!user.emailVerified) {
          await sendVerificationEmail();
        }
        result.ok = true;
      }
    } catch (e) {
      result.error = e;
    }

    return result;
  },

  async resetPassword(email) {
    if (!email) {
      const user = this.getFirebaseUser();
      if (!user) {
        throw "no user";
      }
      email = user.email;
    }

    return Firebase.auth()
      .sendPasswordResetEmail(email)
      .catch(errorHandler);
  }
};
